import React from 'react'
import clsx from 'clsx'
import BasePhoneInput from 'react-phone-input-2'
import { useAppDispatch } from '@SonarCX/hooks/redux'
import { Trash, Back } from 'iconsax-react'
import { IconButton, useTheme } from '@mui/material'

import { useEnvironment } from '@SonarCX/providers/EnvironmentProvider'
import { checkE164 } from '@SonarCX/utilities'
import { useOwner } from '@SonarCX/providers/OwnerProvider'
import { AddErrorAlert } from '@SonarCX/slices/alert'
import { updateChannel } from '@SonarCX/slices/user'
import useStyles from './styles'

export interface AddChannelProps {
  channel?: APIChannel
  channels: APIChannel[]
  onCancel: () => void
  onAdd: () => void
  isPhone?: boolean
  canDelete?: boolean
}

const AddChannel: React.FC<AddChannelProps> = ({
  channel,
  channels = [],
  onCancel,
  onAdd,
  isPhone,
  canDelete
}) => {
  const theme = useTheme()
  const dispatch = useAppDispatch()
  const { owner } = useOwner()
  const { user, setEnvUser } = useEnvironment()
  const [label, setLabel] = React.useState(
    channel?.name ? channel?.name : isPhone ? 'Phone' : 'Email'
  )
  const [value, setValue] = React.useState(channel?.value ?? '')
  const editing = Boolean(channel)
  const focusTimerRef = React.useRef(null)

  const classes = useStyles({ canDelete })

  const getLocale = React.useCallback(() => {
    try {
      const lang = navigator.language.split('-')[1]
      return lang.toLowerCase()
    } catch (error) {
      console.warn('Could not get browser locale, using fallback')
      return 'us'
    }
  }, [])

  const findIfHasRepeatedChannelValue = React.useCallback(
    value => channels.some(chan => chan.value === value),
    [channels]
  )

  const showError = React.useCallback(
    (message: string, delay: number = 2500) =>
      dispatch(AddErrorAlert({ err: { message } }, delay)),
    [dispatch]
  )

  const saveChannel = React.useCallback(
    value => {
      const newChannel = {
        type: (isPhone ? 'sms_voice' : 'email') as APIChannel['type'],
        name: label,
        value: value
      }
      dispatch(
        updateChannel(
          owner.id,
          newChannel,
          channel?.id ?? null,
          newChannels => {
            onAdd()
            onCancel()
            if (user.id === owner.id) {
              setEnvUser({ ...user, channels: [...newChannels] })
            }
          }
        )
      )
    },
    [
      dispatch,
      onAdd,
      onCancel,
      owner,
      label,
      isPhone,
      channel,
      user,
      setEnvUser
    ]
  )

  const handleSaveChannel = React.useCallback(() => {
    let realValue = value
    if (isPhone) {
      const [error, number] = checkE164(value)
      if (error) {
        showError(error)
        return
      }
      realValue = number
    }
    //no actual changes when editing, cancel.
    if (
      editing &&
      channel.name === label &&
      channel.value === realValue.trim()
    ) {
      onCancel()
      return
      //User only changed existing channel name
    } else if (
      editing &&
      channel.name !== label &&
      channel.value === realValue.trim()
    ) {
      saveChannel(realValue)
      return
      //check if channel already exists for user
    } else if (findIfHasRepeatedChannelValue(realValue.trim())) {
      showError(
        `Error adding communication channel. User has existing communication channel with same value: ${realValue.trim()}`,
        5000
      )
      onCancel()
      return
    }

    saveChannel(realValue)
  }, [
    label,
    value,
    isPhone,
    showError,
    onCancel,
    channel,
    editing,
    saveChannel,
    findIfHasRepeatedChannelValue
  ])

  const handleKeyDown = React.useCallback(
    async (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Escape') {
        localStorage.setItem('canceled', 'true')
        onCancel()
        return
      }
      if (e.key !== 'Enter') {
        return
      }
      handleSaveChannel()
    },
    [onCancel, handleSaveChannel]
  )

  //add timeout to enable navigating between inputs
  const handleOnBlur = React.useCallback(() => {
    const canceledPressed = localStorage.getItem('canceled')

    if (canceledPressed === 'true') {
      localStorage.removeItem('canceled')
      return
    }

    if (label && value) {
      focusTimerRef.current = setTimeout(() => {
        handleSaveChannel()
      }, 250)
      return
    }
  }, [label, value, handleSaveChannel])

  const handleOnFocus = React.useCallback(() => {
    clearTimeout(focusTimerRef.current)
  }, [])

  return (
    <div className={classes.root}>
      <input
        placeholder='label'
        value={label}
        onChange={e => setLabel(e.target.value)}
        onKeyDown={handleKeyDown}
        autoFocus
        className={clsx(classes.input, classes.label)}
        onBlur={handleOnBlur}
        onFocus={handleOnFocus}
      />

      {isPhone ? (
        <BasePhoneInput
          placeholder='phone number'
          value={value}
          onChange={val => setValue(val)}
          onKeyDown={handleKeyDown}
          disableSearchIcon
          disableDropdown
          prefix=''
          country={getLocale()}
          containerClass={classes.phoneInputContainer}
          onBlur={handleOnBlur}
          onFocus={handleOnFocus}
        />
      ) : (
        <input
          placeholder='email address'
          value={value}
          onChange={e => setValue(e.target.value)}
          onKeyDown={handleKeyDown}
          className={clsx(classes.input, classes.email)}
          onBlur={handleOnBlur}
          onFocus={handleOnFocus}
        />
      )}

      <IconButton onClick={onCancel}>
        {channel ? (
          <Back variant='Bold' size={theme.size.base} />
        ) : (
          <Trash variant='Bold' size={theme.size.base} />
        )}
      </IconButton>
    </div>
  )
}

export default AddChannel
